import type { Listing } from "@lumora/core";
import { useCallback, useEffect, useRef, useState } from "react";
import { appCover } from "../apps/appListings.js";
import { useAppListings } from "../apps/useAppListings.js";
import { nextIndex, prevIndex, storeButtonState, type StoreButtonState } from "../apps/appGallery.js";
import "./apps.css";

function StoreButton({ state, variant }: { state: StoreButtonState; variant: "apple" | "play" }) {
  const cls = `app-store-btn app-store-btn--${variant}`;
  return (
    <div className="app-store-item">
      {state.enabled ? (
        <a className={cls} href={state.href} target="_blank" rel="noopener noreferrer">
          {state.label}
        </a>
      ) : (
        <button type="button" className={cls} disabled aria-disabled="true" title={state.caption}>
          {state.label}
        </button>
      )}
      {state.enabled ? null : <span className="app-store-caption">{state.caption}</span>}
    </div>
  );
}

function AppModal({ listing, onClose }: { listing: Listing; onClose: () => void }) {
  const [index, setIndex] = useState(0);
  const dialogRef = useRef<HTMLDivElement>(null);
  const images = listing.images;
  const n = images.length;

  useEffect(() => {
    const prevFocus = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
      prevFocus?.focus?.();
    };
  }, []);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") setIndex((i) => nextIndex(i, n));
      else if (e.key === "ArrowLeft") setIndex((i) => prevIndex(i, n));
    },
    [n, onClose],
  );

  return (
    <div className="app-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className="app-modal"
        role="dialog"
        aria-modal="true"
        aria-label={listing.title}
        tabIndex={-1}
        ref={dialogRef}
        onKeyDown={onKeyDown}
      >
        <button type="button" className="app-modal-close" aria-label="Close" onClick={onClose}>
          &times;
        </button>
        <h2>{listing.title}</h2>
        <p>{listing.description}</p>
        {n > 0 ? (
          <>
            <div className="app-gallery-stage">
              {n > 1 ? (
                <button type="button" className="app-gallery-nav app-gallery-prev" aria-label="Previous screenshot" onClick={() => setIndex((i) => prevIndex(i, n))}>
                  &lsaquo;
                </button>
              ) : null}
              <div className="app-gallery-main">
                <img src={images[index]} alt={`${listing.title} screenshot ${index + 1} of ${n}`} />
              </div>
              {n > 1 ? (
                <button type="button" className="app-gallery-nav app-gallery-next" aria-label="Next screenshot" onClick={() => setIndex((i) => nextIndex(i, n))}>
                  &rsaquo;
                </button>
              ) : null}
            </div>
            {n > 1 ? (
              <div className="app-gallery-thumbs">
                {images.map((src, i) => (
                  <button
                    type="button"
                    key={src}
                    className="app-gallery-thumb"
                    aria-label={`Show screenshot ${i + 1}`}
                    aria-current={i === index ? "true" : undefined}
                    onClick={() => setIndex(i)}
                  >
                    <img src={src} alt="" loading="lazy" />
                  </button>
                ))}
              </div>
            ) : null}
          </>
        ) : null}
        <div className="app-stores">
          <StoreButton variant="apple" state={storeButtonState("App Store", listing.fields.appStoreUrl)} />
          <StoreButton variant="play" state={storeButtonState("Google Play", listing.fields.playStoreUrl)} />
        </div>
      </div>
    </div>
  );
}

export interface AppsPageProps {
  className?: string;
  listings?: readonly Listing[];
  /** Test hook: render with this listing's modal already open. */
  initialOpenId?: string | null;
}

export function AppsPage({ className = "site-apps", listings: listingsProp, initialOpenId = null }: AppsPageProps) {
  const { status, listings } = useAppListings(listingsProp);
  const [openId, setOpenId] = useState<string | null>(initialOpenId);
  const open = listings.find((l) => l.id === openId) ?? null;
  return (
    <section className={className} data-lumora-view="apps">
      <h1>Apps</h1>
      <p className="site-apps-lead">Apps built by Bahaa. Tap one to see screenshots and where to get it.</p>
      {listings.length === 0 ? (
        <p className="site-apps-empty" role="status">
          {status === "loading" ? "Loading apps..." : status === "error" ? "Apps could not be loaded right now. Please try again soon." : "No apps yet"}
        </p>
      ) : null}
      <div className="apps-grid">
        {listings.map((l) => (
          <button type="button" key={l.id} className="app-card" onClick={() => setOpenId(l.id)}>
            <img src={appCover(l)} alt="" loading="lazy" />
            <span className="app-card-body">
              <span className="app-card-title">{l.title}</span>
              <span className="app-card-summary">{l.summary}</span>
              <span className="app-card-platform">{l.fields.platform}</span>
            </span>
          </button>
        ))}
      </div>
      {open ? <AppModal listing={open} onClose={() => setOpenId(null)} /> : null}
    </section>
  );
}
